import { useEffect, useState } from 'react';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Coverholder {
  company_id: string;
  company_name: string;
  dba_name: string;
  claim_count: number;
  active_yn: string;
}

interface BdxSheet {
  name: string;
  claims: number;
}

interface BdxResult {
  file_base64: string;
  file_name: string;
  company_name: string;
  period: string;
  row_count: number;
  column_count: number;
  sheet_count: number;
  sheets: BdxSheet[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildBdxFilename(companyName: string, period: string): string {
  // Sanitize company name: keep alphanumeric and spaces, collapse spaces to underscore
  const safeName = companyName
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .replace(/\s+/g, '_');

  // Parse period end date into Mon_YYYY (e.g. "2025-01-31" -> "Jan_2025")
  let periodLabel = '';
  if (period) {
    const d = new Date(period + 'T00:00:00');
    if (!isNaN(d.getTime())) {
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      periodLabel = `${months[d.getMonth()]}_${d.getFullYear()}`;
    }
  }

  const parts = [safeName, 'Bordereaux'];
  if (periodLabel) parts.push(periodLabel);
  return parts.join('_') + '.xlsx';
}

function getLastDayOfPreviousMonth(): string {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
  return lastDay.toISOString().split('T')[0];
}

function getLastDayOfCurrentMonth(): string {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return lastDay.toISOString().split('T')[0];
}

function downloadBase64File(base64: string, fileName: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BdxGenerator() {
  const [coverholders, setCoverholders] = useState<Coverholder[]>([]);
  const [loadingCoverholders, setLoadingCoverholders] = useState(true);
  const [coverholderError, setCoverholderError] = useState<string | null>(null);

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [periodEnd, setPeriodEnd] = useState(getLastDayOfPreviousMonth());
  const [invoiceDate, setInvoiceDate] = useState(getLastDayOfCurrentMonth());

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [result, setResult] = useState<BdxResult | null>(null);

  // Derive the display name of the selected coverholder for the progress message
  const selectedCoverholder = coverholders.find(
    (c) => c.company_id === selectedCompanyId
  );

  // ── Load coverholders on mount ─────────────────────────────────────────────
  useEffect(() => {
    setLoadingCoverholders(true);
    setCoverholderError(null);

    api
      .getCoverholders()
      .then((data: { coverholders: Coverholder[]; count: number }) => {
        setCoverholders(data.coverholders);
        if (data.coverholders.length > 0) {
          setSelectedCompanyId(data.coverholders[0].company_id);
        }
      })
      .catch((err: Error) => {
        setCoverholderError(err.message || 'Failed to load coverholders.');
      })
      .finally(() => {
        setLoadingCoverholders(false);
      });
  }, []);

  // ── Generate BDX ──────────────────────────────────────────────────────────
  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedCompanyId) return;

    setGenerating(true);
    setGenerateError(null);
    setResult(null);

    try {
      const data: BdxResult = await api.generateBdx(
        selectedCompanyId,
        periodEnd,
        invoiceDate
      );
      setResult(data);
    } catch (err: any) {
      setGenerateError(err.message || 'BDX generation failed.');
    } finally {
      setGenerating(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-8 max-w-3xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
          BDX Generator
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Generate bordereau reports for Lloyd's Market submission
        </p>
      </div>

      {/* Form card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-5">
          Report Parameters
        </h2>

        {loadingCoverholders ? (
          <div className="py-8">
            <LoadingSpinner message="Loading coverholders..." />
          </div>
        ) : coverholderError ? (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            <span className="font-semibold">Error loading coverholders:</span>{' '}
            {coverholderError}
          </div>
        ) : (
          <form onSubmit={handleGenerate} className="space-y-5">
            {/* Coverholder */}
            <div>
              <label
                htmlFor="coverholder"
                className="block text-sm font-medium text-slate-700 mb-1.5"
              >
                Coverholder
              </label>
              <select
                id="coverholder"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                disabled={generating}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
              >
                {coverholders.length === 0 && (
                  <option value="">No coverholders available</option>
                )}
                {coverholders.map((ch) => (
                  <option key={ch.company_id} value={ch.company_id}>
                    {ch.company_name}
                    {ch.dba_name && ch.dba_name !== ch.company_name
                      ? ` (${ch.dba_name})`
                      : ''}{' '}
                    — {ch.claim_count.toLocaleString()} claim
                    {ch.claim_count !== 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Date row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="period-end"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Period End
                </label>
                <input
                  id="period-end"
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  disabled={generating}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                />
              </div>
              <div>
                <label
                  htmlFor="invoice-date"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Invoice Date
                </label>
                <input
                  id="invoice-date"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  disabled={generating}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                />
              </div>
            </div>

            {/* Generate button */}
            <div className="pt-1">
              <button
                type="submit"
                disabled={generating || !selectedCompanyId}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-150"
              >
                {generating ? (
                  <>
                    <span
                      className="w-4 h-4 rounded-full border-2 border-blue-300 border-t-white animate-spin flex-shrink-0"
                      aria-hidden="true"
                    />
                    Generating...
                  </>
                ) : (
                  'Generate BDX'
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Progress state */}
      {generating && selectedCoverholder && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div
              className="w-8 h-8 rounded-full border-4 border-blue-200 border-t-blue-500 animate-spin flex-shrink-0"
              role="status"
              aria-label="Generating BDX"
            />
            <div>
              <p className="text-sm font-medium text-slate-700">
                Generating BDX for{' '}
                <span className="text-blue-600">
                  {selectedCoverholder.company_name}
                </span>
                ...
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                This may take a moment depending on claim volume.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {generateError && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 mb-6">
          <p className="text-sm font-semibold text-red-800 mb-0.5">
            Generation failed
          </p>
          <p className="text-sm text-red-700">{generateError}</p>
        </div>
      )}

      {/* Success card */}
      {result && !generating && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Success header */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100 bg-green-50">
            <div
              className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0"
              aria-hidden="true"
            >
              <svg
                className="w-4 h-4 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">
                BDX Generated Successfully
              </p>
              <p className="text-xs text-slate-500 mt-0.5">{result.file_name}</p>
            </div>
          </div>

          {/* Summary stats */}
          <div className="px-6 py-5">
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Coverholder
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {result.company_name}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Period
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {result.period}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Rows
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {result.row_count.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Columns
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">
                  {result.column_count.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Sheets */}
            <div className="mb-5">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                Sheets ({result.sheet_count})
              </p>
              <ul className="space-y-1.5">
                {result.sheets.map((sheet, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-100 px-3 py-2"
                  >
                    <span className="text-sm font-medium text-slate-700">
                      {sheet.name}
                    </span>
                    <span className="text-xs text-slate-500">
                      {sheet.claims.toLocaleString()} claim
                      {sheet.claims !== 1 ? 's' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Download button */}
            <button
              onClick={() => {
                const filename =
                  result.file_name && result.file_name !== 'bdx.xlsx' && result.file_name !== 'download.xlsx'
                    ? result.file_name
                    : buildBdxFilename(result.company_name, result.period);
                downloadBase64File(result.file_base64, filename);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-150"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download Excel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
